import { useState } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Heart, Copy, CheckCircle2, Smartphone, Building2, 
  QrCode, IndianRupee
} from "lucide-react";

const DonationPage = () => {
  const [copiedField, setCopiedField] = useState(null);

  // Donation Details
  const UPI_ID = "7070301098@upi";
  const PAYEE_NAME = "LUNAWAT FOUNDATION";
  const QR_CODE_URL = "https://customer-assets.emergentagent.com/job_lunawat-connect/artifacts/f601a2v3_IMG-20251230-WA0000.jpg";
  
  // UPI Payment URL (generic format that works with all apps)
  const getUPIUrl = (app = 'generic') => {
    const params = new URLSearchParams({
      pa: UPI_ID,
      pn: PAYEE_NAME,
      cu: 'INR',
      mode: '02',
      purpose: '00'
    });
    
    switch(app) {
      case 'gpay':
        return `tez://upi/pay?${params.toString()}`;
      case 'phonepe':
        return `phonepe://pay?${params.toString()}`;
      case 'paytm':
        return `paytmmp://pay?${params.toString()}`;
      default:
        return `upi://pay?${params.toString()}`;
    }
  };

  const BANK_DETAILS = {
    accountHolder: "LUNAWAT FOUNDATION",
    accountNumber: "50200086717279",
    ifsc: "HDFC0004201",
    branch: "IGATPURI BRANCH",
    accountType: "CURRENT",
    bankName: "HDFC BANK"
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied to clipboard!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, field }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => copyToClipboard(text, field)}
      className="h-8 px-2"
    >
      {copiedField === field ? (
        <CheckCircle2 className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </Button>
  );

  const DetailRow = ({ label, value, copyable = true }) => (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
      {copyable && <CopyButton text={value} field={label} />}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="donation-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Support LUNAWAT FOUNDATION</h1>
          <p className="text-muted-foreground">
            Your generous donation helps us serve the community better
          </p>
        </div>

        {/* UPI Payment Section */}
        <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">UPI Payment</CardTitle>
                <CardDescription>Quick and easy payment via UPI</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* QR Code */}
            <div className="flex flex-col items-center mb-6">
              <div className="p-4 bg-white rounded-xl shadow-lg mb-4">
                <img 
                  src={QR_CODE_URL} 
                  alt="UPI QR Code" 
                  className="w-48 h-48 object-contain"
                />
              </div>
              <Badge variant="secondary" className="mb-2">
                <QrCode className="w-3 h-3 mr-1" />
                Scan QR Code to Pay
              </Badge>
            </div>

            {/* UPI ID */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                  <IndianRupee className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">UPI ID</p>
                  <p className="font-semibold text-lg">7070301098@upi</p>
                </div>
              </div>
              <CopyButton text="7070301098@upi" field="UPI ID" />
            </div>

            {/* Pay Now Button */}
            <a 
              href={getUPIUrl('generic')}
              className="flex items-center justify-center gap-2 w-full mt-4 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <IndianRupee className="w-5 h-5" />
              Pay Now via UPI
            </a>

            {/* UPI Apps */}
            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground mb-3">Or pay using specific app</p>
              <div className="flex flex-wrap justify-center gap-2">
                <a 
                  href={getUPIUrl('gpay')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/512px-Google_Pay_Logo.svg.png" alt="GPay" className="w-6 h-6 object-contain" />
                  Google Pay
                </a>
                <a 
                  href={getUPIUrl('phonepe')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <img src="https://cdn.iconscout.com/icon/free/png-256/free-phonepe-2709167-2249157.png" alt="PhonePe" className="w-6 h-6 object-contain" />
                  PhonePe
                </a>
                <a 
                  href={getUPIUrl('paytm')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <img src="https://cdn.iconscout.com/icon/free/png-256/free-paytm-226448.png" alt="Paytm" className="w-6 h-6 object-contain" />
                  Paytm
                </a>
                <a 
                  href={getUPIUrl('generic')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/UPI-Logo-vector.svg/1200px-UPI-Logo-vector.svg.png" alt="BHIM" className="w-6 h-6 object-contain" />
                  BHIM UPI
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Tap to open app and complete payment
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bank Transfer Section */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Bank Transfer (NEFT/RTGS/IMPS)</CardTitle>
                <CardDescription>Transfer directly to our bank account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <DetailRow label="Account Holder Name" value={BANK_DETAILS.accountHolder} />
              <DetailRow label="Bank Name" value={BANK_DETAILS.bankName} />
              <DetailRow label="Account Number" value={BANK_DETAILS.accountNumber} />
              <DetailRow label="IFSC Code" value={BANK_DETAILS.ifsc} />
              <DetailRow label="Branch" value={BANK_DETAILS.branch} />
              <DetailRow label="Account Type" value={BANK_DETAILS.accountType} copyable={false} />
            </div>

            {/* Copy All Button */}
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => {
                const allDetails = `Account Holder: ${BANK_DETAILS.accountHolder}\nBank: ${BANK_DETAILS.bankName}\nAccount No: ${BANK_DETAILS.accountNumber}\nIFSC: ${BANK_DETAILS.ifsc}\nBranch: ${BANK_DETAILS.branch}\nAccount Type: ${BANK_DETAILS.accountType}`;
                copyToClipboard(allDetails, "All Bank Details");
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy All Bank Details
            </Button>
          </CardContent>
        </Card>

        {/* Thank You Note */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-6 text-center">
            <div className="text-4xl mb-3">🙏</div>
            <h3 className="font-semibold text-lg mb-2">Thank You for Your Support!</h3>
            <p className="text-sm text-muted-foreground">
              Every contribution, big or small, makes a difference. 
              Your donation helps us continue our mission of serving the Lunawat community.
            </p>
          </CardContent>
        </Card>

        {/* Helpline */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            For any donation queries, contact us at
          </p>
          <a 
            href="tel:7070301098" 
            className="text-primary font-semibold hover:underline"
          >
            📞 7070301098
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default DonationPage;
